import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SectionedPageLayoutItem<TId extends string> {
  id: TId;
  label: string;
  icon?: LucideIcon;
  description?: string;
  badge?: ReactNode;
  disabled?: boolean;
}

export interface SectionedPageLayoutProps<TId extends string> {
  title: string;
  description?: string;
  items: readonly SectionedPageLayoutItem<TId>[];
  activeId: TId;
  onActiveChange: (id: TId) => void;
  children: ReactNode;
  sidebarLabel?: string;
  headerActions?: ReactNode;
  className?: string;
  sidebarClassName?: string;
  contentClassName?: string;
}

export function SectionedPageLayout<TId extends string>({
  title,
  description,
  items,
  activeId,
  onActiveChange,
  children,
  sidebarLabel = 'Sections',
  headerActions,
  className,
  sidebarClassName,
  contentClassName,
}: SectionedPageLayoutProps<TId>): React.ReactElement {
  return (
    <div className={cn('flex flex-1 overflow-hidden bg-background', className)}>
      <aside
        className={cn(
          'flex w-64 shrink-0 flex-col border-r border-border bg-surface overflow-hidden',
          sidebarClassName
        )}
      >
        <div className="border-b border-border px-4 py-3.5">
          <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-text-tertiary">
            {sidebarLabel}
          </div>
          <h1 className="mt-1 truncate text-sm font-semibold text-foreground">{title}</h1>
          {description && (
            <p className="mt-1 line-clamp-2 text-[11.5px] leading-5 text-text-tertiary">
              {description}
            </p>
          )}
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
          {items.map(item => {
            const isActive = item.id === activeId;

            return (
              <button
                key={item.id}
                type="button"
                disabled={item.disabled}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => {
                  onActiveChange(item.id);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md border px-2.5 py-2 text-left text-sm transition-colors',
                  isActive
                    ? 'border-accent-bright/30 bg-[oklch(0.72_0.14_225/0.12)] text-accent-bright'
                    : 'border-transparent text-text-secondary hover:bg-surface-elevated hover:text-text-primary',
                  item.disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
                )}
              >
                {item.icon && <item.icon className="size-3.5 shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-[12.5px] font-medium',
                      isActive ? 'text-text-primary' : 'text-text-secondary'
                    )}
                  >
                    {item.label}
                  </span>
                  {item.description && (
                    <span className="mt-0.5 block truncate text-[10.5px] text-text-tertiary">
                      {item.description}
                    </span>
                  )}
                </span>
                {item.badge && <span className="shrink-0">{item.badge}</span>}
              </button>
            );
          })}
        </nav>
      </aside>

      <main className={cn('flex min-w-0 flex-1 flex-col overflow-hidden', contentClassName)}>
        {headerActions && (
          <div className="flex shrink-0 items-center justify-end border-b border-border px-5 py-3">
            {headerActions}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
      </main>
    </div>
  );
}
